import { useState, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
    Plus,
    ChevronRight,
    ChevronDown,
    FilePlus,
    Folder,
    FileText,
    Trash2,
} from 'lucide-react'

interface OutlineContentProps {
    draftId: string
}

interface OutlineItem {
    id: string
    type: 'act' | 'sequence' | 'scene' | 'beat'
    title: string
    description?: string
    children?: OutlineItem[]
    expanded?: boolean
    notes?: string
}

const TYPE_ICONS = {
    act: Folder,
    sequence: Folder,
    scene: FileText,
    beat: FileText,
}

const TYPE_COLORS = {
    act: 'text-blue-400',
    sequence: 'text-purple-400',
    scene: 'text-green-400',
    beat: 'text-zinc-400',
}

const TYPE_LABELS = {
    act: 'Act',
    sequence: 'Sequence',
    scene: 'Scene',
    beat: 'Beat',
}

let nextId = 100
const generateId = () => `outline-${++nextId}`

export default function OutlineContent({ draftId: _draftId }: OutlineContentProps) {
    const [outline, setOutline] = useState<OutlineItem[]>([
        {
            id: '1',
            type: 'act',
            title: 'Act One - Setup',
            expanded: true,
            children: [
                {
                    id: '1.1',
                    type: 'sequence',
                    title: 'Opening Sequence',
                    description: 'The audience meets the protagonist and sees their ordinary world.',
                    expanded: true,
                    children: [
                        { id: '1.1.1', type: 'scene', title: 'INT. APARTMENT - MORNING', description: 'We meet our protagonist waking up to their daily routine.' },
                        { id: '1.1.2', type: 'scene', title: 'EXT. CITY STREET - DAY', description: 'The ordinary world is established. We sense something is about to change.' },
                    ],
                },
                {
                    id: '1.2',
                    type: 'sequence',
                    title: 'Catalyst Sequence',
                    description: 'The inciting incident that launches the story.',
                    children: [
                        { id: '1.2.1', type: 'scene', title: 'INT. OFFICE - DAY', description: 'The inciting incident occurs, disrupting the status quo.' },
                    ],
                },
            ],
        },
        {
            id: '2',
            type: 'act',
            title: 'Act Two - Confrontation',
            description: 'The protagonist faces obstacles and grows.',
            children: [
                {
                    id: '2.1',
                    type: 'sequence',
                    title: 'Fun and Games',
                    description: 'The promise of the premise is explored.',
                    children: [
                        { id: '2.1.1', type: 'scene', title: 'Various Locations - MONTAGE', description: 'A montage showing the protagonist exploring their new reality.' },
                    ],
                },
            ],
        },
        {
            id: '3',
            type: 'act',
            title: 'Act Three - Resolution',
            description: 'The climax and denouement.',
            children: [],
        },
    ])

    const [selectedItem, setSelectedItem] = useState<string | null>(null)
    const [showAddMenu, setShowAddMenu] = useState(false)

    // Find item by ID in the tree
    const findItem = useCallback((items: OutlineItem[], id: string): OutlineItem | null => {
        for (const item of items) {
            if (item.id === id) return item
            if (item.children) {
                const found = findItem(item.children, id)
                if (found) return found
            }
        }
        return null
    }, [])

    const currentItem = useMemo(() => {
        if (!selectedItem) return null
        return findItem(outline, selectedItem)
    }, [selectedItem, outline, findItem])

    const toggleExpanded = (id: string) => {
        const toggleItem = (items: OutlineItem[]): OutlineItem[] => {
            return items.map(item => {
                if (item.id === id) {
                    return { ...item, expanded: !item.expanded }
                }
                if (item.children) {
                    return { ...item, children: toggleItem(item.children) }
                }
                return item
            })
        }
        setOutline(toggleItem(outline))
    }

    // Update an item in the tree
    const updateItem = (id: string, updates: Partial<OutlineItem>) => {
        const doUpdate = (items: OutlineItem[]): OutlineItem[] => {
            return items.map(item => {
                if (item.id === id) {
                    return { ...item, ...updates }
                }
                if (item.children) {
                    return { ...item, children: doUpdate(item.children) }
                }
                return item
            })
        }
        setOutline(doUpdate(outline))
    }

    // Delete an item from the tree
    const deleteItem = (id: string) => {
        const doDelete = (items: OutlineItem[]): OutlineItem[] => {
            return items
                .filter(item => item.id !== id)
                .map(item => {
                    if (item.children) {
                        return { ...item, children: doDelete(item.children) }
                    }
                    return item
                })
        }
        setOutline(doDelete(outline))
        if (selectedItem === id) setSelectedItem(null)
    }

    // Add a new item
    const addItem = (type: OutlineItem['type']) => {
        const newItem: OutlineItem = {
            id: generateId(),
            type,
            title: type === 'act' ? `New Act` : type === 'sequence' ? 'New Sequence' : 'New Scene',
            description: '',
            children: type === 'act' || type === 'sequence' ? [] : undefined,
            expanded: true,
        }

        if (type === 'act') {
            setOutline([...outline, newItem])
        } else if (selectedItem) {
            const addChild = (items: OutlineItem[]): OutlineItem[] => {
                return items.map(item => {
                    if (item.id === selectedItem && item.children) {
                        return { ...item, children: [...item.children, newItem], expanded: true }
                    }
                    if (item.children) {
                        return { ...item, children: addChild(item.children) }
                    }
                    return item
                })
            }
            setOutline(addChild(outline))
        } else {
            if (outline.length > 0) {
                const updated = outline.map((item, i) =>
                    i === outline.length - 1
                        ? { ...item, children: [...(item.children || []), newItem] }
                        : item
                )
                setOutline(updated)
            }
        }

        setSelectedItem(newItem.id)
        setShowAddMenu(false)
    }

    // Count total items recursively
    const countItems = (items: OutlineItem[]): number => {
        let count = 0
        for (const item of items) {
            count++
            if (item.children) count += countItems(item.children)
        }
        return count
    }

    const renderOutlineItem = (item: OutlineItem, depth: number = 0) => {
        const Icon = TYPE_ICONS[item.type]
        const hasChildren = item.children && item.children.length > 0

        return (
            <div key={item.id}>
                <div
                    className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors group",
                        selectedItem === item.id ? "bg-primary/20 ring-1 ring-primary/40" : "hover:bg-accent"
                    )}
                    style={{ paddingLeft: `${depth * 16 + 8}px` }}
                    onClick={() => setSelectedItem(item.id)}
                >
                    {hasChildren ? (
                        <button
                            onClick={(e) => { e.stopPropagation(); toggleExpanded(item.id) }}
                            className="p-0.5 hover:bg-accent rounded"
                        >
                            {item.expanded ? (
                                <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            ) : (
                                <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            )}
                        </button>
                    ) : (
                        <div className="w-4" />
                    )}
                    <Icon className={cn("h-4 w-4", TYPE_COLORS[item.type])} />
                    <span className="text-sm flex-1 truncate">{item.title}</span>
                    <button
                        onClick={(e) => { e.stopPropagation(); deleteItem(item.id) }}
                        className="h-6 w-6 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-destructive/20 rounded transition-opacity"
                        title="Delete"
                    >
                        <Trash2 className="h-3 w-3 text-destructive" />
                    </button>
                </div>

                {hasChildren && item.expanded && (
                    <div>
                        {item.children!.map(child => renderOutlineItem(child, depth + 1))}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="h-full flex bg-background">
            {/* Outline Tree */}
            <div className="w-80 border-r flex flex-col">
                <div className="flex items-center justify-between p-3 border-b">
                    <div className="flex items-center gap-2">
                        <h2 className="text-sm font-medium">Outline</h2>
                        <span className="text-[10px] text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                            {countItems(outline)} items
                        </span>
                    </div>
                    <div className="relative">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setShowAddMenu(!showAddMenu)}
                        >
                            <Plus className="h-4 w-4" />
                        </Button>
                        {showAddMenu && (
                            <div className="absolute right-0 top-full mt-1 z-50 bg-popover border rounded-md shadow-lg py-1 min-w-[140px]">
                                {(['act', 'sequence', 'scene'] as const).map(type => (
                                    <button
                                        key={type}
                                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors flex items-center gap-2"
                                        onClick={() => addItem(type)}
                                    >
                                        {type === 'act' || type === 'sequence' ? (
                                            <Folder className={cn("h-3.5 w-3.5", TYPE_COLORS[type])} />
                                        ) : (
                                            <FileText className={cn("h-3.5 w-3.5", TYPE_COLORS[type])} />
                                        )}
                                        Add {TYPE_LABELS[type]}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 overflow-auto p-2">
                    {outline.map(item => renderOutlineItem(item))}
                </div>
            </div>

            {/* Detail Panel */}
            <div className="flex-1 p-6">
                {currentItem ? (
                    <div className="max-w-xl">
                        {/* Type badge */}
                        <div className="flex items-center gap-2 mb-4">
                            <span className={cn(
                                "text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded",
                                currentItem.type === 'act' ? "bg-blue-500/20 text-blue-400" :
                                    currentItem.type === 'sequence' ? "bg-purple-500/20 text-purple-400" :
                                        currentItem.type === 'scene' ? "bg-green-500/20 text-green-400" :
                                            "bg-zinc-500/20 text-zinc-400"
                            )}>
                                {TYPE_LABELS[currentItem.type]}
                            </span>
                        </div>

                        {/* Title */}
                        <Input
                            className="text-lg font-medium border-none bg-transparent focus-visible:ring-0 px-0 mb-2 text-foreground"
                            value={currentItem.title}
                            onChange={(e) => updateItem(currentItem.id, { title: e.target.value })}
                            placeholder={`${TYPE_LABELS[currentItem.type]} title...`}
                        />

                        {/* Description */}
                        <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2 block">Description</label>
                        <textarea
                            className="w-full h-32 p-3 rounded-lg border bg-card resize-none text-sm focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-colors"
                            value={currentItem.description || ''}
                            onChange={(e) => updateItem(currentItem.id, { description: e.target.value })}
                            placeholder="Scene description, notes, or content..."
                        />

                        {/* Notes */}
                        <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium mb-2 block mt-4">Notes</label>
                        <textarea
                            className="w-full h-24 p-3 rounded-lg border bg-card resize-none text-sm focus:ring-1 focus:ring-primary/50 focus:border-primary/50 transition-colors"
                            value={currentItem.notes || ''}
                            onChange={(e) => updateItem(currentItem.id, { notes: e.target.value })}
                            placeholder="Private notes..."
                        />

                        {/* Children count */}
                        {currentItem.children && (
                            <div className="mt-6 pt-4 border-t">
                                <span className="text-xs text-muted-foreground">
                                    {currentItem.children.length} child {currentItem.children.length === 1 ? 'item' : 'items'}
                                </span>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 mt-6">
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => deleteItem(currentItem.id)}
                            >
                                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                                Delete {TYPE_LABELS[currentItem.type]}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <FilePlus className="h-12 w-12 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-medium mb-2">Select an item</h3>
                        <p className="text-sm text-muted-foreground max-w-xs">
                            Click on an item in the outline to view or edit its details.
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
